/**
 * The Delivery & Progress card's delivery risk, as the SO page shows it (2026-10-23, Mike, SO-1138).
 *
 * jobq-api GET /sales_orders/{id}/production_progress judges an order by the tasks that EXIST:
 * "complete" once every released task is done. A line with no job (or too few pieces in
 * production) has no task, so SO-1138 read "Production Complete · Finished on the promised date"
 * while SPILLPLATFORMBRACKET (60 ordered) had no job and the order list said Needs More Jobs.
 *
 * `shortLines` = the page's yieldViolations (the red shortage rows: an "Ordered" yield policy and a
 * line whose pieces in production + shipped fall short), so the card agrees with the table under it
 * and with the list's Needs More Jobs (same rule). With short lines:
 *   complete / on_track / at_risk / no_date → "needs_jobs" (the dates the API gave are kept);
 *   overdue / behind stay (already red, more urgent) with the short lines added to the reason.
 * Without short lines the API's answer comes back unchanged.
 */

const OVERRIDDEN = new Set(["complete", "on_track", "at_risk", "no_date"]);

/** "SPILLPLATFORMBRACKET needs 60 more" / "2 lines need more jobs: A 60, B 5" (at most 3 named). */
export function shortLinesText(shortLines) {
  const rows = (shortLines || []).filter((v) => v && Number(v.shortage) > 0);
  if (rows.length === 0) return null;
  const name = (v) => v.product_id || `part #${v.part_id}`;
  if (rows.length === 1) return `${name(rows[0])} needs ${rows[0].shortage} more`;
  const named = rows.slice(0, 3).map((v) => `${name(v)} ${v.shortage}`).join(", ");
  const more = rows.length > 3 ? ` +${rows.length - 3} more` : "";
  return `${rows.length} lines need more jobs: ${named}${more}`;
}

export function effectiveDeliveryRisk(deliveryRisk, shortLines) {
  if (!deliveryRisk) return deliveryRisk;
  const text = shortLinesText(shortLines);
  if (!text) return deliveryRisk;
  if (OVERRIDDEN.has(deliveryRisk.risk_level)) {
    return { ...deliveryRisk, risk_level: "needs_jobs", risk_reason: text, api_risk_level: deliveryRisk.risk_level };
  }
  const reason = deliveryRisk.risk_reason ? `${deliveryRisk.risk_reason} · ${text}` : text;
  return { ...deliveryRisk, risk_reason: reason };
}
